import type {
  NdjsonThought,
  NdjsonCommand,
  NdjsonCommandResult,
  NdjsonException,
  NdjsonLine,
  AgentThought,
  AgentCommand,
  AgentCommandResult,
  AgentException,
} from './AgentSession';

type RovodevThoughtChild =
  | RovodevCommand
  | RovodevCommandResult
  | RovodevException;

export class RovodevException implements AgentException {
  readonly type: 'exception';
  readonly message: string;
  readonly title?: string;
  readonly exceptionType?: string;

  constructor(props: NdjsonException) {
    const { exception_type, ...rest } = props;
    Object.assign(this, {
      ...rest,
      exceptionType: exception_type,
    });
    Object.freeze(this);
  }
}

export class RovodevCommandResult implements AgentCommandResult {
  readonly id: string;
  readonly type = 'command_result' as const;
  readonly contentParts: string[];

  constructor(props: NdjsonCommandResult) {
    Object.assign(this, {
      id: props.id,
      type: props.type,
      contentParts: props.content ?? [],
    });
    Object.freeze(this);
  }

  get content(): string {
    return this.contentParts.join('');
  }
}

export class RovodevCommand implements AgentCommand {
  readonly id: string;
  readonly type = 'command' as const;
  readonly contentParts: string[];
  readonly toolName: string;
  readonly args: string[] = [];
  readonly filePaths: string[] = [];

  constructor(props: NdjsonCommand) {
    let args;
    let filePaths;

    try {
      if (props.content?.length) {
        const parsedArgs = JSON.parse(props.content.join(''));
        args = Object.keys(parsedArgs).map(argKey => {
          if (argKey === 'file_paths') filePaths = parsedArgs[argKey];
          return `${argKey}: ${JSON.stringify(parsedArgs[argKey])}`;
        });
      }
    } catch {
      // If JSON.parse fails simply skip parsing of args, shouldn't happen in practise
    } finally {
      Object.assign(this, {
        id: props.id,
        type: props.type,
        contentParts: props.content ?? [],
        toolName: props.tool_name,
        args,
        filePaths: filePaths ?? [],
      });
      Object.freeze(this);
    }
  }

  get content(): string {
    return this.contentParts.join('');
  }
}

export class RovodevThought implements AgentThought {
  readonly id: string;
  readonly type = 'thought' as const;
  readonly contentParts: string[] = [];
  readonly children: RovodevThoughtChild[] = [];

  constructor(props: NdjsonThought) {
    Object.assign(this, {
      id: props.id,
      type: props.type,
      contentParts: props.content,
    });
    Object.freeze(this);
  }

  public appendContent(content: string[]) {
    this.contentParts.push(...content);
  }

  public addChild(line: NdjsonLine) {
    if (line.type === 'command_result') {
      this.children.push(new RovodevCommandResult(line));
    }

    if (line.type === 'command') {
      this.children.push(new RovodevCommand(line));
    }
  }

  get content(): string {
    return this.contentParts.join('');
  }
}

export type RovodevSession = RovodevThought[];
